/**
 * useBarberStats Hook
 *
 * Calculates real-time statistics for the barber dashboard:
 * - Total tickets in queue
 * - Total estimated wait time
 * - Next client information
 * - Tickets completed today
 */

import { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { QueueTicket } from '../types';

export interface BarberStats {
  totalInQueue: number;
  totalTime: number;
  nextClient: {
    ticketNumber: string;
    position: number;
    clientName?: string;
    clientEmail?: string;
  } | null;
  completedToday: number;
}

interface UseBarberStatsProps {
  barberId: string;
  branchId: string;
  tickets: QueueTicket[];
}

/**
 * Custom hook to calculate barber dashboard statistics
 *
 * @param barberId - The barber's user ID
 * @param branchId - The branch ID where the barber works
 * @param tickets - Current queue tickets (active tickets)
 * @returns BarberStats object with all metrics
 */
export function useBarberStats({ barberId, branchId, tickets }: UseBarberStatsProps): BarberStats {
  const [completedToday, setCompletedToday] = useState<number>(0);

  // Calculate statistics from current queue tickets
  const totalInQueue = tickets.length;

  const totalTime = tickets.reduce((acc, ticket) => {
    return acc + (ticket.serviceDuration || 30);
  }, 0);

  // Find next client in waiting (exclude in_service and completed)
  const waitingTickets = tickets
    .filter(t => t.status === 'waiting' || t.status === 'arrived' || t.status === 'notified')
    .sort((a, b) => a.position - b.position);

  const nextClient = waitingTickets.length > 0
    ? {
        ticketNumber: waitingTickets[0].ticketNumber || '0',
        position: waitingTickets[0].position,
        clientName: waitingTickets[0].clientName,
        clientEmail: waitingTickets[0].clientEmail,
      }
    : null;

  // Subscribe to completed tickets from today
  useEffect(() => {
    // Calculate today's start time (00:00:00)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTimestamp = Timestamp.fromDate(today);

    // Query completed tickets from today for this barber
    const completedQuery = query(
      collection(db, 'queues'),
      where('barberId', '==', barberId),
      where('branchId', '==', branchId),
      where('status', '==', 'completed'),
      where('completedAt', '>=', todayTimestamp)
    );

    const unsubscribe = onSnapshot(
      completedQuery,
      (snapshot) => {
        setCompletedToday(snapshot.size);
      },
      (error) => {
        console.error('Error fetching completed tickets:', error);
        setCompletedToday(0);
      }
    );

    return () => unsubscribe();
  }, [barberId, branchId]);

  return {
    totalInQueue,
    totalTime,
    nextClient,
    completedToday,
  };
}
